import { Controller } from "@hotwired/stimulus"

const DEFAULT_WIDTH = 520
const MIN_WIDTH = 320
const MAX_WIDTH_RATIO = 0.8
const ANIMATION_MS = 300

export default class extends Controller<HTMLElement> {
  static targets = ["wrapper", "panel", "content"]

  declare readonly wrapperTarget: HTMLElement
  declare readonly panelTarget: HTMLElement
  declare readonly contentTarget: HTMLElement

  private opener: HTMLElement | null = null

  connect(): void {
    this.panelTarget.style.width = `${DEFAULT_WIDTH}px`
  }

  show(event: Event): void {
    event.preventDefault()
    const trigger = event.currentTarget as HTMLAnchorElement
    this.opener = trigger
    this.contentTarget.replaceChildren(this.frameFor(trigger))
    this.wrapperTarget.classList.remove("hidden")
    this.wrapperTarget.setAttribute("aria-hidden", "false")
    requestAnimationFrame(() => this.panelTarget.classList.remove("translate-x-full"))
    this.panelTarget.focus()
  }

  close(): void {
    this.panelTarget.classList.add("translate-x-full")
    window.setTimeout(() => {
      this.wrapperTarget.classList.add("hidden")
      this.wrapperTarget.setAttribute("aria-hidden", "true")
      this.contentTarget.replaceChildren()
      this.opener?.focus()
    }, ANIMATION_MS)
  }

  closeWithKeyboard(event: KeyboardEvent): void {
    if (event.key === "Escape" && !this.wrapperTarget.classList.contains("hidden")) this.close()
  }

  startResize(event: MouseEvent): void {
    event.preventDefault()
    const resize = (move: MouseEvent): void => {
      const width = Math.min(window.innerWidth * MAX_WIDTH_RATIO, Math.max(MIN_WIDTH, window.innerWidth - move.clientX))
      this.panelTarget.style.width = `${width}px`
    }
    const stop = (): void => {
      document.body.style.userSelect = ""
      document.removeEventListener("mousemove", resize)
      document.removeEventListener("mouseup", stop)
    }
    document.body.style.userSelect = "none"
    document.addEventListener("mousemove", resize)
    document.addEventListener("mouseup", stop)
  }

  private frameFor(trigger: HTMLAnchorElement): HTMLElement {
    const frame = document.createElement("turbo-frame")
    frame.id = "slide_panel"
    frame.setAttribute("src", trigger.href)
    frame.setAttribute("target", "_top")
    frame.textContent = "Loading…"
    frame.className = "block p-6 text-sm text-fg-muted"
    return frame
  }
}
